<template>
  <ContentWrap>
    <el-form ref="queryFormRef" :inline="true" :model="queryParams" class="-mb-15px" label-width="80px">
      <el-form-item label="单号" prop="no">
        <el-input v-model="queryParams.no" class="!w-200px" clearable @keyup.enter="handleQuery" />
      </el-form-item>
      <el-form-item label="创建时间" prop="createTime">
        <el-date-picker
          v-model="queryParams.createTime"
          class="!w-240px"
          type="daterange"
          value-format="YYYY-MM-DD HH:mm:ss"
          start-placeholder="开始"
          end-placeholder="结束"
          :default-time="[new Date('1 00:00:00'), new Date('1 23:59:59')]"
        />
      </el-form-item>
      <el-form-item>
        <el-button @click="handleQuery"><Icon class="mr-5px" icon="ep:search" />搜索</el-button>
        <el-button @click="resetQuery"><Icon class="mr-5px" icon="ep:refresh" />重置</el-button>
      </el-form-item>
    </el-form>
  </ContentWrap>

  <ContentWrap>
    <el-table v-loading="loading" :data="list" :stripe="true" :show-overflow-tooltip="true">
      <el-table-column align="center" label="编号" prop="id" width="70" />
      <el-table-column align="center" label="单号" prop="no" min-width="140" />
      <el-table-column align="center" label="状态" width="90">
        <template #default="{ row }">
          <el-tag :type="tagTypeOf(SHAREHOLDER_SETTLE_STATUS, row.status)" size="small">
            {{ labelOf(SHAREHOLDER_SETTLE_STATUS, row.status) }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column align="center" label="池金额" min-width="100">
        <template #default="{ row }">￥{{ fenToYuan(row.poolAmount || 0) }}</template>
      </el-table-column>
      <el-table-column align="center" label="应发合计" min-width="100">
        <template #default="{ row }">￥{{ fenToYuan(row.bonusAmount || 0) }}</template>
      </el-table-column>
      <el-table-column align="center" label="订单数" prop="orderCount" width="80" />
      <el-table-column align="center" label="股东数" prop="partnerCount" width="80" />
      <el-table-column
        align="center"
        label="创建时间"
        prop="createTime"
        :formatter="dateFormatter"
        width="170"
      />
      <el-table-column align="center" label="操作" fixed="right" width="200">
        <template #default="{ row }">
          <el-button link type="primary" @click="openDetail(row.id)">详情</el-button>
          <el-button
            v-if="fixedStatus === 0"
            v-hasPermi="['distribution:shareholder:settle:confirm']"
            link
            type="success"
            @click="handleConfirm(row)"
          >
            确认
          </el-button>
          <el-button
            v-if="fixedStatus === 0"
            v-hasPermi="['distribution:shareholder:settle:cancel']"
            link
            type="danger"
            @click="handleCancel(row)"
          >
            作废
          </el-button>
          <el-button
            v-if="fixedStatus === 1"
            v-hasPermi="['distribution:shareholder:settle:settle']"
            link
            type="success"
            @click="handleSettle(row)"
          >
            结算入账
          </el-button>
        </template>
      </el-table-column>
    </el-table>
    <Pagination
      v-model:limit="queryParams.pageSize"
      v-model:page="queryParams.pageNo"
      :total="total"
      @pagination="getList"
    />
  </ContentWrap>

  <ShareholderSettleDetailDialog ref="detailRef" @success="getList" />
</template>

<script lang="ts" setup>
import { dateFormatter } from '@/utils/formatTime'
import { fenToYuan } from '@/utils'
import * as SettleApi from '@/api/mall/trade/shareholder/settle'
import { SHAREHOLDER_SETTLE_STATUS, labelOf, tagTypeOf } from '../constants'
import ShareholderSettleDetailDialog from './ShareholderSettleDetailDialog.vue'

const props = defineProps<{ fixedStatus: number }>()

const message = useMessage()
const loading = ref(false)
const list = ref<SettleApi.ShareholderSettleBillVO[]>([])
const total = ref(0)
const queryFormRef = ref()
const detailRef = ref()
const queryParams = reactive({
  pageNo: 1,
  pageSize: 10,
  status: props.fixedStatus,
  no: undefined as string | undefined,
  createTime: undefined as string[] | undefined
})

const getList = async () => {
  loading.value = true
  try {
    queryParams.status = props.fixedStatus
    const data = await SettleApi.getShareholderSettlePage(queryParams)
    list.value = data.list || []
    total.value = data.total || 0
  } finally {
    loading.value = false
  }
}

const handleQuery = () => {
  queryParams.pageNo = 1
  getList()
}

const resetQuery = () => {
  queryFormRef.value?.resetFields()
  handleQuery()
}

const openDetail = (id?: number) => {
  if (id) detailRef.value?.open(id)
}

const handleConfirm = async (row: SettleApi.ShareholderSettleBillVO) => {
  await message.confirm(`确认结算单 ${row.no}？确认后不可再调额。`)
  await SettleApi.confirmShareholderSettle(row.id!)
  message.success('已确认')
  await getList()
}

const handleCancel = async (row: SettleApi.ShareholderSettleBillVO) => {
  await message.confirm(`作废结算单 ${row.no}？将解锁订单。`)
  await SettleApi.cancelShareholderSettle(row.id!)
  message.success('已作废')
  await getList()
}

const handleSettle = async (row: SettleApi.ShareholderSettleBillVO) => {
  await message.confirm(`执行结算入账 ${row.no}？`)
  await SettleApi.settleShareholderSettle(row.id!)
  message.success('已入账')
  await getList()
}

onMounted(getList)
</script>
